// 9fbef606107a605d69c0edbcd8029e5d
import { createSelector } from 'reselect';
import { getCurrencyAttributes } from '@tcp/core/src/reduxStore/selectors/currency.selectors';
import { currencyConversion } from '@tcp/core/src/components/features/CnC/CartItemTile/utils/utils';
import { parseBoolean } from '@tcp/core/src/utils';
import { getIsPlccShippingAddressUpdate } from '../../../../BagPage/container/BagPage.selectors';
import {
  estimatedTaxBagPageEnabled,
  getIsInternationalShipping,
} from '../../../../../../../reduxStore/selectors/session.selectors';
import { getIsGuest } from '../../../../../account/User/container/User.selectors';

export const getCartPage = createSelector(
  (state) => state.CartPageReducer,
  (cartPage) => {
    return cartPage;
  }
);

export const getItemsTotalCount = createSelector(getCartPage, (cartPage) => {
  return cartPage.getIn(['orderDetails', 'totalItems']) || 0;
});
export const getCouponsTotal = createSelector(getCartPage, (cartPage) => {
  return cartPage.getIn(['orderDetails', 'couponsTotal']) || 0;
});
export const getApplePayDisc = createSelector(getCartPage, (cartPage) => {
  return cartPage.getIn(['orderDetails', 'applePayDisc']) || 0;
});
export const getSavingsTotal = createSelector(getCartPage, (cartPage) => {
  return cartPage.getIn(['orderDetails', 'savingsTotal']) || 0;
});
export const getShippingTotal = createSelector(getCartPage, (cartPage) => {
  return cartPage.getIn(['orderDetails', 'shippingTotal']) || 0;
});
export const getTotalTax = createSelector(getCartPage, (cartPage) => {
  return cartPage.getIn(['orderDetails', 'totalTax']) || 0;
});
export const getGrandTotal = createSelector(getCartPage, (cartPage) => {
  return cartPage.getIn(['orderDetails', 'grandTotal']) || 0;
});
export const getProductTotal = createSelector(getCartPage, (cartPage) => {
  return cartPage.getIn(['orderDetails', 'productTotal']) || 0;
});
export const getGiftCardsTotal = createSelector(getCartPage, (cartPage) => {
  return cartPage.getIn(['orderDetails', 'giftCardsTotal']) || 0;
});
export const getTotalOrderSavings = createSelector(getCartPage, (cartPage) => {
  return cartPage.getIn(['orderDetails', 'totalOrderSavings']) || 0;
});
export const getSubTotal = createSelector(getCartPage, (cartPage) => {
  return cartPage.getIn(['orderDetails', 'subTotal']) || 0;
});
export const getGiftServiceTotal = createSelector(getCartPage, (cartPage) => {
  return cartPage.getIn(['orderDetails', 'giftWrappingTotal']) || 0;
});
export const getGiftDiscountTotal = createSelector(getCartPage, (cartPage) => {
  return cartPage.getIn(['orderDetails', 'giftDiscountTotal']) || 0;
});
export const getPointsToRedeem = createSelector(getCartPage, (cartPage) => {
  return cartPage.getIn(['orderDetails', 'pointsToRedeem']) || '0';
});
export const getIsOrderHasShipping = createSelector(getCartPage, (cartPage) => {
  return !!cartPage.getIn(['orderDetails', 'isShippingOrder']);
});
export const getCalculatedEstimatedTax = createSelector(getCartPage, (cartPage) => {
  return cartPage.get('calculatedEstimatedTax');
});
export const getZipForTaxEstimation = createSelector(getCartPage, (cartPage) => {
  return cartPage.get('zipForTaxEstimation');
});
export const getShippingValues = createSelector(getCartPage, (cartPage) => {
  return cartPage.getIn(['orderDetails', 'checkout', 'shipping'], '') || {};
});

export const getCurrencySymbol = createSelector(
  (state) => state.session && state.session.siteDetails.currency,
  (currency) => {
    // eslint-disable-next-line no-nested-ternary
    return currency ? (currency === 'USD' || currency === 'CAD' ? '$' : currency) : '$';
  }
);
export const getOrderItems = createSelector(getCartPage, (cartPage) => {
  return cartPage.getIn(['orderDetails', 'orderItems']) || [];
});
export const getGiftCardsItemsTotalPrice = createSelector(getOrderItems, (orderItemsList) => {
  let totalPrice = 0;
  if (orderItemsList) {
    const orderItems = JSON.parse(JSON.stringify(orderItemsList));
    totalPrice = orderItems?.reduce((total, item) => {
      if (item?.productInfo?.isGiftCard) {
        // eslint-disable-next-line no-param-reassign
        total += item.itemInfo?.quantity * item.itemInfo?.salePrice;
      }
      return total;
    }, 0);
  }
  return totalPrice;
});

export const getLedgerSummaryData = createSelector(
  [
    getItemsTotalCount,
    getSubTotal,
    getCouponsTotal,
    getSavingsTotal,
    getGiftServiceTotal,
    getGiftDiscountTotal,
    getPointsToRedeem,
    getShippingTotal,
    getTotalTax,
    getGrandTotal,
    getGiftCardsTotal,
    getTotalOrderSavings,
    getCurrencySymbol,
    getIsOrderHasShipping,
    getShippingValues,
    getCalculatedEstimatedTax,
    getZipForTaxEstimation,
    getCurrencyAttributes,
    getGiftCardsItemsTotalPrice,
  ],
  (
    itemsCount,
    subTotal,
    couponsTotal,
    savingsTotal,
    giftServiceTotal,
    giftDiscountTotal,
    pointsToRedeem,
    shippingTotal,
    taxesTotal,
    grandTotal,
    giftCardsTotal,
    totalOrderSavings,
    currencySymbol,
    isOrderHasShipping,
    shippingValues,
    calculatedEstimatedTax,
    zipForTaxEstimation,
    currencyAttributes,
    giftCardsItemsTotalPrice
    // eslint-disable-next-line max-params
  ) => {
    const subTotalValue = currencyConversion(subTotal, currencyAttributes);
    const orderBalanceTotal = currencyConversion(grandTotal - giftCardsTotal, currencyAttributes);
    return {
      itemsCount,
      subTotal: subTotalValue,
      couponsTotal,
      savingsTotal,
      giftServiceTotal,
      giftDiscountTotal,
      pointsToRedeem,
      shippingTotal,
      taxesTotal,
      grandTotal,
      giftCardsTotal,
      orderBalanceTotal,
      totalOrderSavings,
      currencySymbol,
      isOrderHasShipping,
      shippingValues,
      zipForTaxEstimation,
      calculatedEstimatedTax,
      giftCardsItemsTotalPrice,
    };
  }
);

export const getFinalPriceValue = createSelector(
  [
    getLedgerSummaryData,
    (state) => getIsPlccShippingAddressUpdate(state),
    (state) => getIsInternationalShipping(state),
    getCurrencyAttributes,
    (state) => estimatedTaxBagPageEnabled(state),
    (state) => getIsGuest(state),
  ],
  (
    ledgerSummary,
    isPlccShippingAddressUpdate,
    isInternationalShipping,
    currencyAttributes,
    enableEstimatedTaxSiteDetails,
    isGuest
  ) => {
    const {
      subTotal = 0,
      grandTotal = 0,
      giftCardsTotal = 0,
      couponsTotal = 0,
      savingsTotal = 0,
      shippingTotal = 0,
      taxesTotal = 0,
      giftServiceTotal = 0,
      calculatedEstimatedTax = 0,
    } = ledgerSummary || {};

    // Enable estimated tax logic
    const enableEstimatedTax = isGuest
      ? parseBoolean(enableEstimatedTaxSiteDetails.GUEST_ESTIMATED_TAX_BAG_PAGE_ENABLED)
      : parseBoolean(enableEstimatedTaxSiteDetails.ESTIMATED_TAX_BAG_PAGE_ENABLED);

    // Perform currency conversions
    const subTotalPrice = currencyConversion(subTotal, currencyAttributes);
    const orderBalanceTotal = currencyConversion(grandTotal - giftCardsTotal, currencyAttributes);

    // Determine the order balance
    const orderBalance = isInternationalShipping ? orderBalanceTotal : grandTotal;

    // Calculate order balance with tax
    const taxValue = isPlccShippingAddressUpdate ? taxesTotal : Number(calculatedEstimatedTax);

    const orderBalanceTax =
      subTotalPrice +
      taxValue +
      shippingTotal +
      giftServiceTotal -
      giftCardsTotal -
      couponsTotal -
      savingsTotal;

    // Final price value
    const priceValue = enableEstimatedTax ? Number(orderBalanceTax) : Number(orderBalance);
    // Format the final price
    const formattedPriceValue = priceValue < 0 ? '0.00' : priceValue.toFixed(2);
    return {
      formattedPriceValue,
    };
  }
);
